import { isWaiting, stageMismatch, STAGE_MISMATCH_LABELS } from "../lib/schedule";
import { headlineValue, money, splitAddress, type JobCardFaceProps } from "./cardFaceTypes";
import styles from "./CardFaceDTriage.module.css";

export const VARIANT_D_NAME = "Triage — status band first";

/**
 * PROTOTYPE ONLY.
 *
 * Premise: the column already tells you the stage, so the card's job is to
 * answer a different question — "does this need me today?". The Schedule Intent
 * is therefore promoted from a quiet line near the bottom to a tinted band
 * across the top, in one of three states:
 *
 *   amber   — waiting on something; nobody is moving it
 *   green   — a planned action with a date
 *   grey    — unplanned, which is itself a thing to notice and currently
 *             invisible on the live face
 *
 * The cost is a whole line spent on status even when there is nothing to say,
 * and a busier board. The question this variant settles is whether that trade
 * is worth it — it is the one to reject if the board reads as noisy.
 */
export function VariantDTriage({ card }: JobCardFaceProps) {
  const { street, locality } = splitAddress(card.propertyAddress);
  const value = headlineValue(card);
  const waiting = isWaiting(card.scheduleAction ?? "");
  const mismatch = stageMismatch(card.scheduleAction ?? "", card.columnId ?? "");
  const planned = Boolean(card.scheduleLabel);
  const state = !planned ? "none" : waiting ? "waiting" : "planned";

  return (
    <div className={styles.face}>
      <div className={styles.band} data-state={state}>
        <span className={styles.bandText}>
          {state === "none"
            ? "Unplanned"
            : [card.scheduleLabel, card.scheduleWhen].filter(Boolean).join(" · ")}
        </span>
        {mismatch && (
          <span className={styles.flag} title={STAGE_MISMATCH_LABELS[mismatch]}>
            !
          </span>
        )}
        {card.priority && card.priority !== "normal" && (
          <span className={styles.priority} data-priority={card.priority}>
            {card.priority}
          </span>
        )}
      </div>

      <strong className={styles.title} title={card.title}>
        {card.title}
      </strong>
      <span className={styles.address} title={card.propertyAddress}>
        {street}
        {locality && <span className={styles.locality}>, {locality}</span>}
      </span>

      <div className={styles.footer}>
        <span className={styles.client}>{card.clientName ?? "No client"}</span>
        {value ? (
          <span className={styles.value}>
            {money(value.cents)}
            {value.estimated && <em className={styles.est}>est</em>}
          </span>
        ) : (
          <span className={styles.noValue}>Unpriced</span>
        )}
      </div>
    </div>
  );
}
